/**
 * 👑 ADVISOR DEFINITIONS
 *
 * Each advisor unlocks when you research a specific technology.
 * Advisors provide bonuses, penalties, and special abilities.
 */

export const ADVISORS = [
  // ═══════════════════════════════════════════
  // TIER 1-2: FOUNDATIONS & EARLY DEVELOPMENT
  // ═══════════════════════════════════════════
  {
    id: 'agrarian',
    name: 'Elder Thorne',
    icon: '🌾',
    title: 'Master Farmer',
    tier: 1,
    description: 'A wise farmer who has cultivated these lands for generations. His knowledge of the earth brings bountiful harvests but leaves little time for scholarly pursuits.',
    flavor: '"The land provides for those who tend it with care."',
    bonuses: {
      food: 0.50,           // +50% food production
      population: 0.25,     // +25% population growth
    },
    penalties: {
      knowledge: -0.20,     // -20% knowledge production
    },
    special: null,
    unlockTech: null,       // Available from start
    baseCost: { food: 50, wood: 25 },
  },
  {
    id: 'builder',
    name: 'Architect Vela',
    icon: '🏛️',
    title: 'Master Builder',
    tier: 1,
    description: 'A visionary architect whose efficient designs reduce construction costs. Her focus on building leaves research as an afterthought.',
    flavor: '"Form follows function, but beauty follows form."',
    bonuses: {},
    penalties: {
      researchSpeed: -0.15, // -15% research speed
    },
    special: {
      type: 'buildingCost',
      value: -0.20,         // -20% building costs
      description: '-20% building costs',
    },
    unlockTech: 'basic_tools',
    baseCost: { stone: 75, wood: 50 },
  },
  {
    id: 'foreman',
    name: 'Foreman Gralt',
    icon: '⛏️',
    title: 'Mining Overseer',
    tier: 2,
    description: 'A gruff overseer who drives workers to extract more from the earth. His harsh methods boost material output but dampen morale.',
    flavor: '"Dig deeper! There\'s always more below."',
    bonuses: {
      stone: 0.40,          // +40% stone
      wood: 0.30,           // +30% wood
    },
    penalties: {
      culture: -0.25,       // -25% culture (harsh working conditions)
    },
    special: null,
    unlockTech: 'deep_mining',
    baseCost: { stone: 100, gold: 30 },
  },

  // ═══════════════════════════════════════════
  // TIER 3-4: ADVANCEMENT & SPECIALIZATION
  // ═══════════════════════════════════════════
  {
    id: 'scholar',
    name: 'Sage Aldric',
    icon: '📚',
    title: 'Royal Scholar',
    tier: 3,
    description: 'Former head of the Royal Academy. His methodical approach accelerates research but his ivory tower mentality slows practical production.',
    flavor: '"Knowledge is the foundation upon which empires are built."',
    bonuses: {
      knowledge: 0.35,      // +35% knowledge
    },
    penalties: {
      production: -0.10,    // -10% all production
    },
    special: {
      type: 'researchSpeed',
      value: 0.25,          // +25% research speed
      description: '+25% research speed',
    },
    unlockTech: 'writing',
    baseCost: { knowledge: 100, gold: 50 },
  },
  {
    id: 'warlord',
    name: 'General Marcus',
    icon: '⚔️',
    title: 'War Commander',
    tier: 4,
    description: 'A battle-hardened commander who militarizes your economy. Iron and steel flow freely, but the arts suffer under martial law.',
    flavor: '"Peace is merely the time between wars."',
    bonuses: {
      iron: 0.50,           // +50% iron
      steel: 0.40,          // +40% steel
    },
    penalties: {
      culture: -0.30,       // -30% culture
      knowledge: -0.15,     // -15% knowledge
    },
    special: null,
    unlockTech: 'iron_working',
    baseCost: { iron: 80, gold: 100 },
  },
  {
    id: 'merchant',
    name: 'Trader Isolde',
    icon: '💰',
    title: 'Guild Master',
    tier: 4,
    description: 'A shrewd merchant with connections across distant lands. Her trade networks maximize gold but her mercenary approach inflates prices.',
    flavor: '"Everything has a price. I simply know them all."',
    bonuses: {
      gold: 0.60,           // +60% gold
    },
    penalties: {},
    special: {
      type: 'buildingCost',
      value: 0.10,          // +10% building costs (mercenary pricing)
      description: '+10% building costs',
    },
    unlockTech: 'currency',
    baseCost: { gold: 150 },
  },

  // ═══════════════════════════════════════════
  // TIER 5-6: EXPANSION & INDUSTRY
  // ═══════════════════════════════════════════
  {
    id: 'diplomat',
    name: 'Councilor Finn',
    icon: '⚖️',
    title: 'Royal Diplomat',
    tier: 5,
    description: 'A seasoned politician who brings stability to the realm. His balanced approach provides modest gains without any drawbacks.',
    flavor: '"Compromise is not weakness—it is wisdom."',
    bonuses: {
      production: 0.12,     // +12% all production
      culture: 0.15,        // +15% culture
    },
    penalties: {},
    special: null,
    unlockTech: 'philosophy',
    baseCost: { gold: 200, culture: 100 },
  },
  {
    id: 'industrialist',
    name: 'Engineer Cogsworth',
    icon: '⚙️',
    title: 'Chief Engineer',
    tier: 6,
    description: 'A brilliant engineer who mechanizes your production. Tools and machinery flourish, but his factories pollute the cultural landscape.',
    flavor: '"Progress waits for no one. Neither do my machines."',
    bonuses: {
      tools: 0.50,          // +50% tools
      machinery: 0.50,      // +50% machinery
      coal: 0.30,           // +30% coal
    },
    penalties: {
      culture: -0.35,       // -35% culture
    },
    special: null,
    unlockTech: 'industrialization',
    baseCost: { tools: 100, gold: 300 },
  },
  {
    id: 'miner',
    name: 'Deepmaster Durin',
    icon: '💎',
    title: 'Mining Guildmaster',
    tier: 6,
    description: 'A legendary dwarf who knows every vein in the mountain. His deep mines yield precious gems but require intense focus.',
    flavor: '"The mountain\'s heart beats with gems untold."',
    bonuses: {
      coal: 0.45,           // +45% coal
      gems: 0.60,           // +60% gems
      stone: 0.25,          // +25% stone
    },
    penalties: {
      food: -0.20,          // -20% food (underground focus)
    },
    special: null,
    unlockTech: 'advanced_metallurgy',
    baseCost: { coal: 150, gold: 400, gems: 20 },
  },

  // ═══════════════════════════════════════════
  // TIER 7-8: COMMERCE & INNOVATION
  // ═══════════════════════════════════════════
  {
    id: 'banker',
    name: 'Lord Rothwell',
    icon: '🏦',
    title: 'Master Banker',
    tier: 7,
    description: 'A financial mastermind who revolutionizes your economy. His banking systems multiply gold and reduce costs through clever financing.',
    flavor: '"Gold breeds gold, if you know how to plant it."',
    bonuses: {
      gold: 0.80,           // +80% gold
    },
    penalties: {
      population: -0.15,    // -15% population growth (class divide)
    },
    special: {
      type: 'buildingCost',
      value: -0.15,         // -15% building costs
      description: '-15% building costs',
    },
    unlockTech: 'banking',
    baseCost: { gold: 1000, gems: 50 },
  },
  {
    id: 'patron',
    name: 'Duchess Helena',
    icon: '🎭',
    title: 'Royal Patron',
    tier: 7,
    description: 'A noble patron of the arts who elevates your cultural standing. Her salons attract the finest minds but drain the treasury.',
    flavor: '"Art is the soul of civilization."',
    bonuses: {
      culture: 0.70,        // +70% culture
      knowledge: 0.30,      // +30% knowledge
    },
    penalties: {
      gold: -0.25,          // -25% gold (patronage is expensive)
    },
    special: {
      type: 'researchSpeed',
      value: 0.15,          // +15% research speed
      description: '+15% research speed',
    },
    unlockTech: 'grand_architecture',
    baseCost: { culture: 200, gold: 800 },
  },
  {
    id: 'scientist',
    name: 'Professor Kepler',
    icon: '🔬',
    title: 'Chief Scientist',
    tier: 8,
    description: 'A visionary scientist who pushes the boundaries of knowledge. His experiments yield breakthroughs but consume vast resources.',
    flavor: '"The universe reveals its secrets to those who ask the right questions."',
    bonuses: {
      science: 0.60,        // +60% science
      knowledge: 0.40,      // +40% knowledge
    },
    penalties: {
      gold: -0.20,          // -20% gold (expensive experiments)
    },
    special: {
      type: 'researchSpeed',
      value: 0.35,          // +35% research speed
      description: '+35% research speed',
    },
    unlockTech: 'scientific_method',
    baseCost: { science: 150, knowledge: 500 },
  },
  {
    id: 'inventor',
    name: 'Nikola Sparks',
    icon: '⚡',
    title: 'Grand Inventor',
    tier: 8,
    description: 'An eccentric genius whose inventions harness the power of energy itself. His creations are wondrous but dangerous.',
    flavor: '"The future is electric!"',
    bonuses: {
      energy: 0.70,         // +70% energy
      machinery: 0.40,      // +40% machinery
    },
    penalties: {
      population: -0.10,    // -10% population (dangerous work)
    },
    special: {
      type: 'clickPower',
      value: 0.50,          // +50% click power
      description: '+50% click power',
    },
    unlockTech: 'physics',
    baseCost: { energy: 100, machinery: 200, gold: 1500 },
  },

  // ═══════════════════════════════════════════
  // TIER 9-10: MASTERY & LEGENDARY
  // ═══════════════════════════════════════════
  {
    id: 'mystic',
    name: 'Archmage Lyra',
    icon: '🔮',
    title: 'Keeper of Secrets',
    tier: 9,
    description: 'The most powerful mage in the realm. Her arcane knowledge enhances magical production but her otherworldly focus neglects the mundane.',
    flavor: '"Reality is merely a suggestion to those who understand its true nature."',
    bonuses: {
      magic: 0.80,          // +80% magic
      crystals: 0.60,       // +60% crystals
    },
    penalties: {
      iron: -0.30,          // -30% iron
      steel: -0.30,         // -30% steel
    },
    special: {
      type: 'researchSpeed',
      value: 0.20,          // +20% research speed
      description: '+20% research speed',
    },
    unlockTech: 'arcane_arts',
    baseCost: { magic: 200, knowledge: 800 },
  },
  {
    id: 'oracle',
    name: 'Oracle Pythia',
    icon: '👁️',
    title: 'The Seer',
    tier: 10,
    description: 'A mysterious prophet who sees beyond the veil. Her visions boost all production and research.',
    flavor: '"I have seen what is to come. Prepare accordingly."',
    bonuses: {
      production: 0.20,     // +20% all production
    },
    penalties: {},
    special: {
      type: 'researchSpeed',
      value: 0.15,          // +15% research speed
      description: '+15% research speed',
    },
    unlockTech: 'mysticism',
    baseCost: { magic: 400, crystals: 150, artifacts: 30 },
  },
  {
    id: 'artificer',
    name: 'Vulcan the Forger',
    icon: '🔥',
    title: 'Legendary Smith',
    tier: 10,
    description: 'A legendary craftsman said to have learned from the gods themselves. His forge produces materials of unmatched quality.',
    flavor: '"What I create will outlast empires."',
    bonuses: {
      iron: 0.50,           // +50% iron
      steel: 0.50,          // +50% steel
      tools: 0.40,          // +40% tools
      artifacts: 0.80,      // +80% artifacts
    },
    penalties: {
      gold: -0.15,          // -15% gold (gives away masterworks)
    },
    special: null,
    unlockTech: 'artifact_crafting',
    baseCost: { artifacts: 80, steel: 500, gems: 100 },
  },

  // ═══════════════════════════════════════════
  // TIER 11: TRANSCENDENCE
  // ═══════════════════════════════════════════
  {
    id: 'dimensional',
    name: 'The Wanderer',
    icon: '🌌',
    title: 'Dimensional Sage',
    tier: 11,
    description: 'A being who has traveled between worlds. Their cosmic perspective grants unprecedented bonuses to all aspects of your realm.',
    flavor: '"Your dimension is but one thread in an infinite tapestry."',
    bonuses: {
      production: 0.30,     // +30% all production
      magic: 0.50,          // +50% magic
      crystals: 0.50,       // +50% crystals
    },
    penalties: {},
    special: {
      type: 'researchSpeed',
      value: 0.25,          // +25% research speed
      description: '+25% research speed',
    },
    unlockTech: 'dimensional_theory',
    baseCost: { artifacts: 200, crystals: 300, magic: 500 },
  },
];

export default ADVISORS;
